import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { ILawyer, ILawyersResponse, ILawyerState } from "../types/lawyers";

const initialState: ILawyerState = {
  lawyers: [],
  lawyer: null,
  total: 0,
  limit: 10,
  offset: 0,
  loading: false,
  loadingLawyerId: null,
};

export const lawyers = createSlice({
  name: "lawyers",
  initialState,

  reducers: {
    // Set lawyers returned from API
    setLawyers: (state, action: PayloadAction<ILawyersResponse>) => {
      state.lawyers = action.payload.data.items;
      state.total = action.payload.data.total;
      state.limit = action.payload.data.limit;
      state.offset = action.payload.data.offset;
    },

    // Set a single lawyer
    setLawyer: (state, action: PayloadAction<ILawyer>) => {
      state.lawyer = action.payload;
    },

    // Add a new lawyer
    createLawyer: (state, action: PayloadAction<ILawyer>) => {
      if (state.offset === 0) {
        state.lawyers = [
          action.payload,
          ...state.lawyers.filter((item) => item.id !== action.payload.id),
        ].slice(0, state.limit);
      }
      state.total += 1;
    },

    // Update a lawyer
    editLawyer: (state, action: PayloadAction<ILawyer>) => {
      state.lawyers = state.lawyers.map((item) =>
        item.id === action.payload.id ? action.payload : item,
      );
      if (state.lawyer?.id === action.payload.id) {
        state.lawyer = { ...state.lawyer, ...action.payload };
      }
    },

    updateLawyerStatus: (
      state,
      action: PayloadAction<{ id: number; is_active: boolean }>,
    ) => {
      state.lawyers = state.lawyers.map((item) =>
        item.id === action.payload.id
          ? { ...item, is_active: action.payload.is_active }
          : item,
      );
      if (state.lawyer?.id === action.payload.id) {
        state.lawyer.is_active = action.payload.is_active;
      }
    },

    // Delete a lawyer
    deleteLawyer: (state, action: PayloadAction<number>) => {
      state.lawyers = state.lawyers.filter(
        (item) => item.id !== action.payload,
      );
      state.total = Math.max(0, state.total - 1);
      if (state.lawyer?.id === action.payload) {
        state.lawyer = null;
      }
    },

    // Pagination
    setLimit: (state, action: PayloadAction<number>) => {
      state.limit = action.payload;
    },

    setOffset: (state, action: PayloadAction<number>) => {
      state.offset = action.payload;
    },

    // Loading
    setLawyerLoading: (state) => {
      state.loading = true;
    },

    disableLawyerLoading: (state) => {
      state.loading = false;
    },

    // Loading a specific lawyer
    setLoadingLawyerId: (state, action: PayloadAction<number | null>) => {
      state.loadingLawyerId = action.payload;
    },

    // Clear selected lawyer
    clearLawyer: (state) => {
      state.lawyer = null;
    },
  },
});

export const {
  setLawyers,
  setLawyer,
  createLawyer,
  editLawyer,
  updateLawyerStatus,
  deleteLawyer,
  setLimit,
  setOffset,
  setLawyerLoading,
  disableLawyerLoading,
  setLoadingLawyerId,
  clearLawyer,
} = lawyers.actions;

export default lawyers.reducer;
